import { useCallback } from 'react';
import useAxios from '@Apis/useAxios';
import {
	GET_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_OPTIONS_API,
	POST_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_API,
	GET_INVOICE_TRIPLICATE_UNIFORM_CONFIRM_DOC_API,
	PUT_INVOICE_TRIPLICATE_UNIFORM_CONFIRM_API,
	PUT_INVOICE_TRIPLICATE_UNIFORM_RESET_API,
	DELETE_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_CACHE_API,
} from '@Apis/apiPath';

/**
 * 三聯式發票退單折讓相關 API Hook
 * 對應後端 InvoiceTriplicateUniformController
 */
const useInvoiceTriplicateUniformApi = () => {
	const Axios = useAxios();

	/**
	 * 取得三聯式發票退單折讓總覽選項（折讓狀態、店鋪下拉）
	 */
	const getInvoiceTriplicateUniformOverviewOptionsApi = useCallback(
		async () => {
			return await Axios.get(GET_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_OPTIONS_API);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得三聯式發票退單折讓總覽（分頁列表）
	 * @param {object} params TriplicateAllowancePaginationRequestModel
	 */
	const postInvoiceTriplicateUniformOverviewApi = useCallback(
		async (params, options = {}) => {
			return await Axios.post(POST_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_API, params, options);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 下載已上傳的折讓證明書（觸發瀏覽器下載）
	 * 路由為站台根路徑 /orders/...（非 /api/...），回應為檔案串流
	 * @param {number} orderID 訂單代碼
	 * @param {number} orderReturnID 退單代碼
	 * @param {string} [extension='pdf'] 副檔名
	 */
	const getInvoiceTriplicateUniformConfirmDocApi = useCallback(
		async (orderID, orderReturnID, extension = 'pdf') => {
			const url = GET_INVOICE_TRIPLICATE_UNIFORM_CONFIRM_DOC_API(orderID, orderReturnID, extension);
			return await Axios.getPDF(url, {}, { fileName: 'triplicate-uniform-confirm' });
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 送出折讓證明書為核准狀態，並開始退款及折讓流程
	 * @param {number} orderID 訂單代碼
	 * @param {number} orderReturnID 退單代碼
	 */
	const putInvoiceTriplicateUniformConfirmApi = useCallback(
		async (orderID, orderReturnID) => {
			return await Axios.put(PUT_INVOICE_TRIPLICATE_UNIFORM_CONFIRM_API(orderID, orderReturnID));
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 重置折讓證明書狀態
	 * @param {number} orderID 訂單代碼
	 * @param {number} orderReturnID 退單代碼
	 */
	const putInvoiceTriplicateUniformResetApi = useCallback(
		async (orderID, orderReturnID) => {
			return await Axios.put(PUT_INVOICE_TRIPLICATE_UNIFORM_RESET_API(orderID, orderReturnID));
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 清除三聯式發票退單折讓總覽暫存
	 * @param {{ clubID: number }} params ClubIdRequestModel
	 */
	const deleteInvoiceTriplicateUniformOverviewCacheApi = useCallback(
		async (params, options = {}) => {
			return await Axios.delete(DELETE_INVOICE_TRIPLICATE_UNIFORM_OVERVIEW_CACHE_API, params, options);
		},
		// eslint-disable-next-line
		[],
	);

	return {
		getInvoiceTriplicateUniformOverviewOptionsApi,
		postInvoiceTriplicateUniformOverviewApi,
		getInvoiceTriplicateUniformConfirmDocApi,
		putInvoiceTriplicateUniformConfirmApi,
		putInvoiceTriplicateUniformResetApi,
		deleteInvoiceTriplicateUniformOverviewCacheApi,
	};
};

export default useInvoiceTriplicateUniformApi;
